/**
 * rowOps.ts - Contiguous row insert/delete methods.
 *
 * Inserting and deleting rows inside the table wraps the underlying `tableOps` row ops with undo
 * checkpointing. Appending blank rows to the end of the table has its own pure transform.
 */

import produce from 'immer';

import type {Table} from 'notebook/tables/constants/TableConstants';
import {TableIndex} from 'notebook/tables/constants/TableSelectionConstants';
import type {TableMutationResult} from 'notebook/tables/excess/ops/TableOpConstants';
import type {TableMutationParams} from 'notebook/tables/store/logic/mutations/mutationParams';
import {persistTable} from 'notebook/tables/store/logic/mutations/mutationParams';
import {saveSelection, setMultiIndices} from 'notebook/tables/store/logic/mutations/selection';
import TableUtil from 'notebook/tables/TableUtil';
import notifications from 'notifications';

/**
 * Checkpoint a row range as the selection for undo before a structural row edit. The range spans the
 * visible (effective) columns, matching the geometry the row axis cell selects.
 */
function saveRowRangeSelection(params: TableMutationParams, start: number, end: number): void {
  const model = params.getTableModel();
  const {start: sliceStart, end: sliceEnd} = model.singleInputs.effectiveSlices;
  const startIndex: TableIndex = [Math.min(start, end), sliceStart];
  const endIndex: TableIndex = [Math.max(start, end), sliceEnd];
  saveSelection(params, {multiStartIndex: startIndex, multiEndIndex: endIndex, multiSelIndex: startIndex});
}

/**
 * Delete a contiguous range of rows.
 *
 * Checkpoints the deleted rows as the selection for undo, then deletes them. `end` defaults to
 * `start`; no-op when `start` is null.
 */
export function deleteRows(params: TableMutationParams, start: number | null, end: number | null): void {
  const lastRow = end ?? start;
  if (start == null || lastRow == null) {
    return;
  }
  saveRowRangeSelection(params, start, lastRow);
  params.tableOps.deleteTableRows(params.getTableProps().table.name, start, lastRow);
}

/**
 * Insert as many rows above the given range as the range spans.
 *
 * Checkpoints the range as the selection for undo before inserting. `end` defaults to `start`; no-op
 * when `start` is null.
 */
export function insertRowsAbove(params: TableMutationParams, start: number | null, end: number | null): void {
  const lastRow = end ?? start;
  if (start == null || lastRow == null) {
    return;
  }
  saveRowRangeSelection(params, start, lastRow);
  params.tableOps.insertTableRowsAbove(params.getTableProps().table.name, start, lastRow);
}

/**
 * Insert as many rows below the given range as the range spans.
 *
 * Checkpoints the range as the selection for undo before inserting. `end` defaults to `start`; no-op
 * when `start` is null.
 */
export function insertRowsBelow(params: TableMutationParams, start: number | null, end: number | null): void {
  const lastRow = end ?? start;
  if (start == null || lastRow == null) {
    return;
  }
  saveRowRangeSelection(params, start, lastRow);
  params.tableOps.insertTableRowsBelow(params.getTableProps().table.name, start, lastRow);
}

/**
 * Append `numRows` blank rows to the end of `table`. When the table has single inputs, the single
 * input columns are re-merged so they span the new rows.
 */
export function _addBlankRowsTransform(
  table: Table,
  numRows: number,
  hasSingleInputs: boolean
): TableMutationResult {
  const newTable = produce(table, (draftTable) => {
    draftTable.table.push(...TableUtil.makeBlankRows(draftTable, numRows));
    if (hasSingleInputs && numRows > 0) {
      draftTable.table = TableUtil.remergeSingleInputColumns(draftTable).table;
    }
  });
  return {
    table: newTable,
    tableOp: {
      type: 'insert-rows',
      rowIndexes: Array.from({length: numRows}, (_, i) => i + table.table.length),
    },
    affectedCells: [],
  };
}

/**
 * Append up to `numRows` blank rows to the end of the table and select the new last row.
 *
 * The count is limited to `dimensions.maxAddableRows`, with a warning when it is reduced. The last
 * row is checkpointed as the selection for undo before the rows are added, and the new rows are
 * persisted before they are selected so the selection never points at rows that do not exist yet.
 *
 * Returns false, without changing anything, when the table cannot add any more rows.
 */
export function addBlankRows(params: TableMutationParams, numRows: number): boolean {
  const model = params.getTableModel();
  const {table} = params.getTableProps();
  const {maxAddableRows} = model.dimensions;
  let rowsToAdd = numRows;
  if (rowsToAdd > maxAddableRows) {
    if (maxAddableRows === 0) {
      notifications.warn(`Cannot add any more rows to this table.`);
      return false;
    }
    rowsToAdd = maxAddableRows;
    notifications.warn(`Cannot add more than ${rowsToAdd} rows to this table.`);
  }

  params.setState({isInsertingRows: false});
  const lastRow = table.table.length - 1;
  saveRowRangeSelection(params, lastRow, lastRow);

  const result = _addBlankRowsTransform(table, rowsToAdd, Boolean(model.singleInputs.boundaryIndex));
  persistTable(params, result);

  const newLastRow = result.table.table.length - 1;
  const {start: sliceStart, end: sliceEnd} = model.singleInputs.effectiveSlices;
  setMultiIndices(params, {
    start: [newLastRow, sliceStart],
    end: [newLastRow, sliceEnd],
    text: result.table.table[newLastRow][0].text,
  });

  if (rowsToAdd > 0) {
    params.setState({scrollToIndexAfterUpdate: newLastRow});
  }
  return true;
}
